/**
 * Firestore helpers for the AI Planner's per-wedding scratchpad (web adapter).
 * Kept in-app so Vitest can mock `firebase/firestore` the same way as before extraction.
 */

import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  PLANNER_MEMORY_NOTE_MAX_LEN,
  PlannerMemorySchema,
  emptyPlannerMemory,
  pruneBucket,
  type PlannerMemory,
  type PlannerMemoryBucket,
  type PlannerMemoryNote,
} from "@amazing-wedding/shared-types";
import { db } from "@/lib/firebase";

const MEMORY_DOC_ID = "main";

function memoryRef(weddingId: string) {
  return doc(db, "weddings", weddingId, "plannerMemory", MEMORY_DOC_ID);
}

export async function loadPlannerMemory(weddingId: string): Promise<PlannerMemory> {
  const snap = await getDoc(memoryRef(weddingId));
  if (!snap.exists()) return emptyPlannerMemory();
  const result = PlannerMemorySchema.safeParse(snap.data());
  return result.success ? result.data : emptyPlannerMemory();
}

export interface AddNoteInput {
  bucket: PlannerMemoryBucket;
  text: string;
  source: "user" | "planner";
  confidence?: "high" | "low";
}

export async function addMemoryNote(
  weddingId: string,
  input: AddNoteInput,
): Promise<PlannerMemoryNote> {
  const text = input.text.trim().slice(0, PLANNER_MEMORY_NOTE_MAX_LEN);
  if (text === "") throw new Error("Cannot remember an empty note.");
  const current = await loadPlannerMemory(weddingId);
  const note: PlannerMemoryNote = {
    id: crypto.randomUUID(),
    text,
    createdAt: new Date().toISOString(),
    source: input.source,
    ...(input.confidence !== undefined ? { confidence: input.confidence } : {}),
  };
  const nextBucket = pruneBucket([...current[input.bucket], note]);
  const next: PlannerMemory = {
    ...current,
    [input.bucket]: nextBucket,
    updatedAt: new Date().toISOString(),
    version: current.version + 1,
  };
  await setDoc(memoryRef(weddingId), next);
  return note;
}

export async function removeMemoryNote(weddingId: string, noteId: string): Promise<boolean> {
  const current = await loadPlannerMemory(weddingId);
  const buckets: PlannerMemoryBucket[] = ["preferences", "decisions", "followUps", "facts"];
  const next: PlannerMemory = {
    preferences: current.preferences,
    decisions: current.decisions,
    followUps: current.followUps,
    facts: current.facts,
    version: current.version,
  };
  let removed = false;
  for (const bucket of buckets) {
    const filtered = current[bucket].filter((n) => n.id !== noteId);
    if (filtered.length !== current[bucket].length) removed = true;
    next[bucket] = filtered;
  }
  if (!removed) return false;
  next.updatedAt = new Date().toISOString();
  next.version = current.version + 1;
  await setDoc(memoryRef(weddingId), next);
  return true;
}
